<script>
    import { geoAlbers, geoPath, geoCentroid } from 'd3';
    import { feature, mesh } from 'topojson-client';
    import { onMount } from 'svelte';
    import geoData from '../data/us.topo.json';
    import land from '../data/land.topo.json';
    import bounds from '../data/bounds.topo.json';
    import smokeMeta from '../data/smoke/smoke.json';
    import { defaults } from '../utils/colors';

    let {
        // Container width in CSS px (used only for the mobile label threshold).
        width = 1000,
        showStateLabels = false,
        showCityLabels = false,
        cityMajorOnly = false,
        autoplay = true,
        // Animation state, shared with the parent (embed controls / social card).
        frameIndex = $bindable(0),
        playing = $bindable(true),
        ready = $bindable(false),
    } = $props();

    const isMobile = $derived(width < 600);

    // Fixed projected coordinate space; the SVG viewBox + canvas share it, and
    // CSS scales the whole thing responsively.
    const W = 975;
    const H = 610;

    // Smoke frame images (EPSG:4326 WebP), resolved to URLs by Vite.
    const frameModules = import.meta.glob('../data/smoke/*.webp', {
        eager: true,
        query: '?url',
        import: 'default',
    });
    const frameUrl = (file) => frameModules[`../data/smoke/${file}`];

    // Keep only the contiguous US (drop AK, HI, PR, and territories) by testing
    // each feature's centroid, since this topojson has no state identifiers.
    const geometries = geoData.objects.tl_2025_us_state.geometries;
    const inConus = (f) => {
        const [lon, lat] = geoCentroid(f);
        return lon > -125 && lon < -66 && lat > 24 && lat < 50;
    };
    const conusGeometries = geometries.filter((g) => inConus(feature(geoData, g)));
    const conusObject = { type: 'GeometryCollection', geometries: conusGeometries };
    const conus = feature(land, land.objects.ne_10m_admin_0_countries_lakes);
    const innerBorders = mesh(geoData, conusObject, (a, b) => a !== b);
    const outerBorder = mesh(geoData, conusObject, (a, b) => a === b);
    // International boundary lines (US–Canada, US–Mexico, etc.).
    const countryBounds = feature(bounds, bounds.objects.ne_10m_admin_0_boundary_lines_land);

    const projection = geoAlbers().fitSize([W, H], outerBorder);
    const path = geoPath(projection);

    // Label anchors as [lon, lat] (this topojson has no state ids, so we place
    // postal codes at representative label points rather than deriving them).
    const stateLabels = [
        ['AL', -86.8, 32.8], ['AZ', -111.7, 34.3], ['AR', -92.4, 34.9], ['CA', -119.7, 37.2],
        ['CO', -105.5, 39.0], ['CT', -72.7, 41.6], ['DE', -75.5, 39.0], ['FL', -81.7, 28.7],
        ['GA', -83.4, 32.6], ['ID', -114.6, 44.4], ['IL', -89.2, 40.0], ['IN', -86.3, 39.9],
        ['IA', -93.5, 42.0], ['KS', -98.4, 38.5], ['KY', -85.3, 37.5], ['LA', -92.0, 31.1],
        ['ME', -69.2, 45.4], ['MD', -76.8, 39.0], ['MA', -71.8, 42.3], ['MI', -84.7, 43.3],
        ['MN', -94.3, 46.3], ['MS', -89.7, 32.7], ['MO', -92.5, 38.4], ['MT', -109.6, 47.0],
        ['NE', -99.8, 41.5], ['NV', -116.9, 39.3], ['NH', -71.6, 43.7], ['NJ', -74.5, 40.1],
        ['NM', -106.1, 34.4], ['NY', -75.5, 42.9], ['NC', -79.4, 35.6], ['ND', -100.5, 47.5],
        ['OH', -82.8, 40.3], ['OK', -97.5, 35.5], ['OR', -120.6, 43.9], ['PA', -77.8, 40.9],
        ['RI', -71.5, 41.7], ['SC', -80.9, 33.9], ['SD', -100.3, 44.4], ['TN', -86.3, 35.8],
        ['TX', -99.3, 31.5], ['UT', -111.7, 39.3], ['VT', -72.7, 44.1], ['VA', -78.8, 37.5],
        ['WA', -120.4, 47.4], ['WV', -80.6, 38.6], ['WI', -89.9, 44.6], ['WY', -107.5, 43.0],
    ].map(([code, lon, lat]) => ({ code, xy: projection([lon, lat]) }));

    // 15 largest US cities by population.
    const cityLabels = [
        ['New York', -74.006, 40.7128], ['Los Angeles', -118.2437, 34.0522],
        ['Chicago', -87.6298, 41.8781], ['Houston', -95.3698, 29.7604],
        ['Phoenix', -112.074, 33.4484], ['Philadelphia', -75.1652, 39.9526],
        ['San Diego', -117.1611, 32.7157], ['Dallas', -96.797, 32.7767],
        ['Jacksonville', -81.6557, 30.3322], ['San Jose', -121.8863, 37.3382],
        ['Columbus', -82.9988, 39.9612], ['Charlotte', -80.8431, 35.2271],
    ].map(([name, lon, lat]) => ({
        name,
        xy: projection([lon, lat]),
        major: ['Los Angeles', 'New York', 'Chicago', 'Houston'].includes(name),
    }));

    const { west, south, east, north } = smokeMeta.bounds;
    const srcW = smokeMeta.width;
    const srcH = smokeMeta.height;
    const frames = smokeMeta.frames;

    const FRAME_MS = 450; // per-frame duration of the loop

    let canvas;
    let mapping = null;
    const frameImages = [];
    let timer = null;

    // For every output pixel, the source pixel it samples from, via the inverse
    // d3 projection. Computed once (projection is fixed).
    function buildMapping() {
        const m = new Int32Array(W * H).fill(-1);
        for (let y = 0; y < H; y++) {
            for (let x = 0; x < W; x++) {
                const ll = projection.invert([x + 0.5, y + 0.5]);
                if (!ll) continue;
                const [lon, lat] = ll;
                if (lon < west || lon > east || lat < south || lat > north) continue;
                const col = ((lon - west) / (east - west) * srcW) | 0;
                const row = ((north - lat) / (north - south) * srcH) | 0;
                if (col < 0 || col >= srcW || row < 0 || row >= srcH) continue;
                m[y * W + x] = row * srcW + col;
            }
        }
        return m;
    }

    async function loadSource(file) {
        const img = new Image();
        img.src = frameUrl(file);
        await img.decode();
        const off = document.createElement('canvas');
        off.width = srcW;
        off.height = srcH;
        const octx = off.getContext('2d');
        octx.drawImage(img, 0, 0);
        return octx.getImageData(0, 0, srcW, srcH).data;
    }

    function reproject(src, ctx) {
        const out = ctx.createImageData(W, H);
        const o = out.data;
        for (let p = 0; p < W * H; p++) {
            const s = mapping[p];
            if (s < 0) continue;
            const si = s * 4;
            const di = p * 4;
            o[di] = src[si];
            o[di + 1] = src[si + 1];
            o[di + 2] = src[si + 2];
            o[di + 3] = src[si + 3];
        }
        return out;
    }

    function show(i) {
        if (canvas && frameImages[i]) canvas.getContext('2d').putImageData(frameImages[i], 0, 0);
    }

    function startLoop() {
        stopLoop();
        timer = setInterval(() => {
            frameIndex = (frameIndex + 1) % frames.length;
        }, FRAME_MS);
    }

    function stopLoop() {
        if (timer) clearInterval(timer);
        timer = null;
    }

    onMount(() => {
        const ctx = canvas.getContext('2d');
        mapping = buildMapping();

        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        let cancelled = false;

        (async () => {
            for (let i = 0; i < frames.length; i++) {
                const src = await loadSource(frames[i].file);
                if (cancelled) return;
                frameImages[i] = reproject(src, ctx);
                if (i === 0) show(0);
            }
            ready = true;
            if (reduceMotion || !autoplay) playing = false;

            // Frame-stepping hook for the social/GIF export (scripts/social.js).
            // Only active when loaded with ?capture, so production is untouched.
            if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('capture')) {
                playing = false;
                window.__smokeCapture = {
                    count: frames.length,
                    frameMs: FRAME_MS,
                    setFrame: (i) => { frameIndex = i; },
                };
            }
        })();

        return () => {
            cancelled = true;
            stopLoop();
        };
    });

    // Drive the loop from the (parent-controlled) `playing` flag.
    $effect(() => {
        if (ready && playing) startLoop();
        else stopLoop();
    });

    // Paint whenever the frame index changes (loop tick or manual/step change).
    $effect(() => {
        show(frameIndex);
    });
</script>

<div class="stack" style="aspect-ratio: {W} / {H};">
    <svg class="layer" viewBox="0 0 {W} {H}" preserveAspectRatio="xMidYMid meet">
        {#each conus.features as f}
            <path d={path(f)} fill={defaults.gray3}></path>
        {/each}
    </svg>

    <canvas class="layer" bind:this={canvas} width={W} height={H}></canvas>

    <svg class="layer" viewBox="0 0 {W} {H}" preserveAspectRatio="xMidYMid meet">
        <path d={path(countryBounds)} fill="none" stroke={defaults.gray6} stroke-width="1" vector-effect="non-scaling-stroke"></path>
        <path d={path(innerBorders)} fill="none" stroke="#ffffff" stroke-width="0.75" vector-effect="non-scaling-stroke"></path>
    </svg>

    {#if showStateLabels || showCityLabels}
        <!-- Labels in an HTML overlay (positioned by %) so text and dots stay a
             constant pixel size instead of scaling with the SVG. -->
        <div class="layer labels">
            {#if showStateLabels && !isMobile}
                {#each stateLabels as s}
                    {#if s.xy}
                        <span
                            class="state-label"
                            style="left: {(s.xy[0] / W) * 100}%; top: {(s.xy[1] / H) * 100}%"
                        >{s.code}</span>
                    {/if}
                {/each}
            {/if}

            {#if showCityLabels}
                {#each cityLabels as c}
                    {#if c.xy && (!cityMajorOnly || c.major)}
                        <span
                            class="city-dot"
                            style="left: {(c.xy[0] / W) * 100}%; top: {(c.xy[1] / H) * 100}%"
                        ></span>
                        <span
                            class="city-label"
                            style="left: {(c.xy[0] / W) * 100}%; top: {(c.xy[1] / H) * 100}%"
                        >{c.name}</span>
                    {/if}
                {/each}
            {/if}
        </div>
    {/if}
</div>

<style lang="scss">
    .stack {
        position: relative;
        width: 100%;
    }

    .layer {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
    }

    /* Dark text + white halo (text-shadow) stays legible over both light land
       and dark smoke. */
    .labels {
        pointer-events: none;
    }

    .labels span {
        position: absolute;
        white-space: nowrap;
    }

    .state-label {
        transform: translate(-50%, -50%);
        color: #404040;
        font-size: 12px;
        font-weight: 300;
        text-shadow:
            -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff,
            0 -1px 0 #fff, 0 1px 0 #fff, -1px 0 0 #fff, 1px 0 0 #fff;
    }

    .city-dot {
        width: 7px;
        height: 7px;
        transform: translate(-50%, -50%);
        background: #1a1a1a;
        border: 1px solid #fff;
        border-radius: 50%;
    }

    .city-label {
        transform: translate(6px, -50%);
        color: #1a1a1a;
        font-size: 12px;
        text-shadow:
            -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff,
            0 -1px 0 #fff, 0 1px 0 #fff, -1px 0 0 #fff, 1px 0 0 #fff;
    }
</style>
